import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { downloadArea, getCacheStats, clearTileCache, formatCacheSize, calculateTileCount } from '@/lib/cacheManager';
import { getLocalWaypoints, saveLocalWaypoint, deleteLocalWaypoint, getLocalTracks, saveLocalTrack, deleteLocalTrack, getLocalTrack } from '@/lib/localStore';
import MapContainerComponent from '@/components/map/MapContainer';
import FloatingNavBar from '@/components/FloatingNavBar';
import TrackRecordPanel from '@/components/TrackRecordPanel';
import WaypointDrawer from '@/components/WaypointDrawer';
import { CATEGORIES } from '@/components/WaypointDrawer';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  Layers, Trash2, Navigation, MapPin, LogIn, LogOut, ChevronRight, X, Route, Clock, Mountain,
  Download, WifiOff, HardDrive, Loader2,
} from 'lucide-react';

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDistance(meters) {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

function formatDate(isoStr) {
  const d = new Date(isoStr);
  return d.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TILE_OPTIONS = [
  { key: 'gsi', label: '国土地理院', description: '日本の詳細地形図' },
  { key: 'opentopomap', label: 'OpenTopoMap', description: '世界の等高線地図' },
  { key: 'osm', label: 'OpenStreetMap', description: '一般的な地図' },
];

export default function MapView() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Map
  const [tileLayer, setTileLayer] = useState('gsi');
  const [flyTo, setFlyTo] = useState(null);

  // GPS & Recording
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [trackPoints, setTrackPoints] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [totalDistance, setTotalDistance] = useState(0);
  const [currentSpeed, setCurrentSpeed] = useState(null);
  const watchIdRef = useRef(null);

  // Data
  const [waypoints, setWaypoints] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);

  // Drawers
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [trackName, setTrackName] = useState('');

  // Offline & Cache
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [mapBounds, setMapBounds] = useState(null);
  const [cacheStats, setCacheStats] = useState({ tileCount: 0, estimatedSizeKB: 0 });
  const [downloadProgress, setDownloadProgress] = useState(null); // { current, total }
  const [isDownloading, setIsDownloading] = useState(false);

  // Load data on mount / auth change
  useEffect(() => {
    loadWaypoints();
    loadTracks();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Online/Offline detection
  useEffect(() => {
    const onOnline = () => { setIsOnline(true); toast.success('オンラインに復帰しました'); };
    const onOffline = () => { setIsOnline(false); toast('オフラインモードです', { icon: '📡' }); };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Load cache stats
  const refreshCacheStats = useCallback(async () => {
    const stats = await getCacheStats();
    setCacheStats(stats);
  }, []);

  useEffect(() => {
    refreshCacheStats();
  }, [refreshCacheStats]);

  // Refresh cache stats when settings or download drawer is visible
  useEffect(() => {
    if (activeDrawer === 'settings' || activeDrawer === 'download') {
      refreshCacheStats();
      const interval = setInterval(refreshCacheStats, 2000);
      return () => clearInterval(interval);
    }
  }, [activeDrawer, refreshCacheStats]);

  const loadWaypoints = async () => {
    if (!user) { setWaypoints(getLocalWaypoints()); return; }
    try {
      const res = await api.get('/waypoints');
      setWaypoints(res.data);
    } catch (e) {
      console.error('Failed to load waypoints:', e);
    }
  };

  const loadTracks = async () => {
    if (!user) { setTracks(getLocalTracks()); return; }
    try {
      const res = await api.get('/tracks');
      setTracks(res.data);
    } catch (e) {
      console.error('Failed to load tracks:', e);
    }
  };

  // GPS Tracking
  useEffect(() => {
    if (!isRecording || isPaused) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const point = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          alt: pos.coords.altitude,
          timestamp: new Date().toISOString(),
        };
        setCurrentLocation({
          ...point,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,
        });
        setCurrentSpeed(pos.coords.speed);

        setTrackPoints((prev) => {
          if (prev.length > 0) {
            const last = prev[prev.length - 1];
            const dist = haversine(last.lat, last.lng, point.lat, point.lng);
            if (dist > 3) {
              setTotalDistance((d) => d + dist);
              return [...prev, point];
            }
            return prev;
          }
          return [point];
        });
      },
      (err) => {
        console.error('GPS error:', err);
        toast.error('GPS信号を取得できません');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 3000 }
    );

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [isRecording, isPaused]);

  // Timer
  useEffect(() => {
    if (!isRecording || isPaused) return;
    const interval = setInterval(() => setElapsedTime((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isRecording, isPaused]);

  // Watch current location even when not recording
  useEffect(() => {
    if (isRecording) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setCurrentLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          alt: pos.coords.altitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,
        });
      },
      () => {},
      { enableHighAccuracy: false, timeout: 30000, maximumAge: 10000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [isRecording]);

  // Handlers
  const handleStartRecording = () => {
    setTrackPoints([]);
    setTotalDistance(0);
    setElapsedTime(0);
    setIsPaused(false);
    setIsRecording(true);
    toast.success('トラッキング開始');
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    if (trackPoints.length > 1) {
      setActiveDrawer('save-track');
      setTrackName(`Track ${new Date().toLocaleDateString('ja-JP')}`);
    } else {
      toast('トラッキングポイントが少なすぎます');
    }
  };

  const handleSaveTrack = async () => {
    if (!trackName.trim() || trackPoints.length < 2) return;
    const data = { name: trackName.trim(), points: trackPoints, distance: totalDistance, duration: elapsedTime, elevation_gain: 0 };
    try {
      if (user) {
        await api.post('/tracks', data);
      } else {
        saveLocalTrack(data);
      }
      toast.success('トラック保存完了');
      setActiveDrawer(null);
      setTrackPoints([]);
      loadTracks();
    } catch (e) {
      toast.error('保存に失敗しました');
    }
  };

  const handleMapClick = useCallback((position) => {
    if (isRecording) return;
    setSelectedPosition(position);
    setActiveDrawer('waypoint-create');
  }, [isRecording]);

  const handleSaveWaypoint = async (data) => {
    if (user) {
      await api.post('/waypoints', data);
    } else {
      saveLocalWaypoint(data);
    }
    toast.success('ウェイポイント保存完了');
    loadWaypoints();
  };

  const handleDeleteWaypoint = async (wpId) => {
    try {
      if (user) {
        await api.delete(`/waypoints/${wpId}`);
      } else {
        deleteLocalWaypoint(wpId);
      }
      toast.success('削除しました');
      loadWaypoints();
    } catch (e) {
      toast.error('削除に失敗しました');
    }
  };

  const handleDeleteTrack = async (trackId) => {
    try {
      if (user) {
        await api.delete(`/tracks/${trackId}`);
      } else {
        deleteLocalTrack(trackId);
      }
      toast.success('削除しました');
      if (selectedTrack?.id === trackId) setSelectedTrack(null);
      loadTracks();
    } catch (e) {
      toast.error('削除に失敗しました');
    }
  };

  const handleViewTrack = async (trackId) => {
    try {
      let data;
      if (user) {
        const res = await api.get(`/tracks/${trackId}`);
        data = res.data;
      } else {
        data = getLocalTrack(trackId);
        if (!data) throw new Error('not found');
      }
      setSelectedTrack(data);
      setActiveDrawer(null);
      if (data.points?.length > 0) {
        const mid = data.points[Math.floor(data.points.length / 2)];
        setFlyTo({ lat: mid.lat, lng: mid.lng });
      }
    } catch (e) {
      toast.error('トラックの読み込みに失敗しました');
    }
  };

  const handleWaypointClick = (wp) => {
    setFlyTo({ lat: wp.lat, lng: wp.lng });
  };

  // Offline cache handlers
  const MAX_ZOOM_PER_LAYER = { gsi: 18, opentopomap: 17, osm: 19 };

  const handleDownloadArea = async () => {
    if (!mapBounds) {
      toast.error('マップの範囲を取得できません');
      return;
    }
    const currentZoom = mapBounds.zoom;
    const maxZoom = MAX_ZOOM_PER_LAYER[tileLayer] || 18;
    const zoomLevels = [];
    for (let z = currentZoom; z <= maxZoom; z++) {
      zoomLevels.push(z);
    }
    const tileCount = calculateTileCount(mapBounds, zoomLevels);
    if (tileCount > 10000) {
      toast.error(`タイル数が多すぎます（${tileCount}枚）。もう少しズームインしてください`);
      return;
    }
    setIsDownloading(true);
    setDownloadProgress({ current: 0, total: tileCount });
    try {
      const result = await downloadArea(
        mapBounds, tileLayer, zoomLevels,
        (current, total) => setDownloadProgress({ current, total })
      );
      toast.success(`${result.downloaded}枚の新規タイルをキャッシュしました`);
      // Update stats from download result directly
      setCacheStats({ tileCount: result.cacheSize, estimatedSizeKB: result.cacheSize * 15 });
      await new Promise(r => setTimeout(r, 500));
      refreshCacheStats();
    } catch (e) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  };

  const handleClearCache = async () => {
    await clearTileCache();
    setCacheStats({ tileCount: 0, estimatedSizeKB: 0 });
    toast.success('タイルキャッシュをクリアしました');
  };

  const handleBoundsChange = useCallback((bounds) => {
    setMapBounds(bounds);
  }, []);

  const categoryMap = {};
  CATEGORIES.forEach((c) => { categoryMap[c.value] = c; });

  return (
    <div className="map-wrapper" data-testid="map-view">
      <MapContainerComponent
        tileLayer={tileLayer}
        currentLocation={currentLocation}
        waypoints={waypoints}
        trackPoints={isRecording ? trackPoints : []}
        selectedTrack={selectedTrack}
        onMapClick={handleMapClick}
        isRecording={isRecording}
        flyTo={flyTo}
        onWaypointClick={handleWaypointClick}
        onBoundsChange={handleBoundsChange}
        isOnline={isOnline}
      />

      {/* Recording stats panel */}
      {isRecording && (
        <TrackRecordPanel
          elapsedTime={elapsedTime}
          totalDistance={totalDistance}
          altitude={currentLocation?.alt}
          speed={currentSpeed}
          isPaused={isPaused}
        />
      )}

      {/* Selected track indicator */}
      {selectedTrack && !isRecording && (
        <div className="fixed top-4 left-4 right-4 z-[1000] glass-panel rounded-2xl px-4 py-3 flex items-center justify-between animate-slide-up">
          <div className="flex items-center gap-2">
            <Route size={16} className="text-lime-400" />
            <span className="text-sm font-medium">{selectedTrack.name}</span>
          </div>
          <button
            onClick={() => setSelectedTrack(null)}
            className="size-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
            data-testid="close-track-btn"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Floating nav */}
      {!activeDrawer && (
        <FloatingNavBar
          isRecording={isRecording}
          isPaused={isPaused}
          onRecord={handleStartRecording}
          onPause={() => setIsPaused(!isPaused)}
          onStop={handleStopRecording}
          onLayers={() => setActiveDrawer('layers')}
          onHistory={() => setActiveDrawer('history')}
          onWaypoints={() => setActiveDrawer('waypoints')}
          onSettings={() => setActiveDrawer('settings')}
        />
      )}

      {/* Locate me button + Download button */}
      {!isRecording && !activeDrawer && (
        <div className="fixed bottom-24 right-4 z-[1000] flex flex-col gap-2">
          <button
            onClick={() => setActiveDrawer('download')}
            className="size-11 rounded-full glass-panel flex items-center justify-center hover:bg-white/10 transition-all active:scale-95"
            data-testid="download-area-btn"
            title="現在のエリアをダウンロード"
          >
            <Download size={18} className="text-orange-400" />
          </button>
          {currentLocation && (
            <button
              onClick={() => setFlyTo({ lat: currentLocation.lat, lng: currentLocation.lng })}
              className="size-11 rounded-full glass-panel flex items-center justify-center hover:bg-white/10 transition-all active:scale-95"
              data-testid="locate-me-btn"
            >
              <Navigation size={18} className="text-blue-400" />
            </button>
          )}
        </div>
      )}

      {/* Offline indicator */}
      {!isOnline && (
        <div className="fixed top-4 right-4 z-[1000] glass-panel rounded-full px-3 py-1.5 flex items-center gap-1.5 animate-slide-up" data-testid="offline-indicator">
          <WifiOff size={14} className="text-yellow-400" />
          <span className="text-xs font-medium text-yellow-400">OFFLINE</span>
        </div>
      )}

      {/* Download progress overlay */}
      {isDownloading && downloadProgress && (
        <div className="fixed top-4 left-4 right-4 z-[1000] glass-panel rounded-2xl px-4 py-3 animate-slide-up" data-testid="download-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 size={16} className="text-orange-400 animate-spin" />
              <span className="text-sm font-medium">ダウンロード中...</span>
            </div>
            <span className="text-xs text-zinc-400">
              {downloadProgress.current} / {downloadProgress.total}
            </span>
          </div>
          <Progress
            value={(downloadProgress.current / downloadProgress.total) * 100}
            className="h-1.5"
          />
        </div>
      )}

      {/* ===== DRAWERS ===== */}

      {/* Waypoint Creation */}
      <WaypointDrawer
        open={activeDrawer === 'waypoint-create'}
        onClose={() => setActiveDrawer(null)}
        position={selectedPosition}
        onSave={handleSaveWaypoint}
      />

      {/* Save Track Drawer */}
      <Drawer open={activeDrawer === 'save-track'} onOpenChange={(o) => !o && setActiveDrawer(null)}>
        <DrawerContent className="glass-panel border-white/10" data-testid="save-track-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">
              トラック保存
            </DrawerTitle>
            <DrawerDescription>
              {formatDistance(totalDistance)} / {formatTime(elapsedTime)} / {trackPoints.length} pts
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4">
            <Input
              value={trackName}
              onChange={(e) => setTrackName(e.target.value)}
              placeholder="トラック名"
              className="bg-white/5 border-white/10 h-11"
              data-testid="track-name-input"
            />
          </div>
          <DrawerFooter>
            <Button
              onClick={handleSaveTrack}
              className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              data-testid="save-track-btn"
            >
              保存
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Layers Drawer */}
      <Drawer open={activeDrawer === 'layers'} onOpenChange={(o) => !o && setActiveDrawer(null)}>
        <DrawerContent className="glass-panel border-white/10" data-testid="layers-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <Layers size={18} /> マップレイヤー
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-2">
            {TILE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => { setTileLayer(opt.key); setActiveDrawer(null); }}
                className={`w-full flex items-center justify-between p-4 rounded-xl transition-all active:scale-[0.98] ${
                  tileLayer === opt.key
                    ? 'bg-orange-500/15 ring-1 ring-orange-500/40'
                    : 'bg-white/5 hover:bg-white/8'
                }`}
                data-testid={`layer-${opt.key}`}
              >
                <div className="text-left">
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-xs text-zinc-400">{opt.description}</div>
                </div>
                {tileLayer === opt.key && (
                  <div className="size-2 rounded-full bg-orange-500" />
                )}
              </button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>

      {/* Track History Drawer */}
      <Drawer open={activeDrawer === 'history'} onOpenChange={(o) => !o && setActiveDrawer(null)}>
        <DrawerContent className="glass-panel border-white/10 max-h-[70vh]" data-testid="history-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <Clock size={18} /> トラック履歴
            </DrawerTitle>
          </DrawerHeader>
          <ScrollArea className="px-4 pb-6 max-h-[50vh]">
            {tracks.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 text-sm">
                トラックがありません
              </div>
            ) : (
              <div className="space-y-2">
                {tracks.map((track) => (
                  <div
                    key={track.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/8 transition-colors group"
                  >
                    <button
                      onClick={() => handleViewTrack(track.id)}
                      className="flex-1 text-left"
                      data-testid={`track-item-${track.id}`}
                    >
                      <div className="text-sm font-medium">{track.name}</div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-zinc-400">
                        <span>{formatDistance(track.distance)}</span>
                        <span>{formatTime(track.duration)}</span>
                        <span>{formatDate(track.created_at)}</span>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDeleteTrack(track.id)}
                      className="size-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                      data-testid={`delete-track-${track.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DrawerContent>
      </Drawer>

      {/* Waypoint List Drawer */}
      <Drawer open={activeDrawer === 'waypoints'} onOpenChange={(o) => !o && setActiveDrawer(null)}>
        <DrawerContent className="glass-panel border-white/10 max-h-[70vh]" data-testid="waypoints-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <MapPin size={18} /> ウェイポイント
            </DrawerTitle>
          </DrawerHeader>
          <ScrollArea className="px-4 pb-6 max-h-[50vh]">
            {waypoints.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 text-sm">
                地図をタップしてウェイポイントを追加
              </div>
            ) : (
              <div className="space-y-2">
                {waypoints.map((wp) => {
                  const cat = categoryMap[wp.category] || { label: wp.category, color: '#f97316' };
                  return (
                    <div
                      key={wp.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/8 transition-colors group"
                    >
                      <div
                        className="size-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: `${cat.color}20`, border: `1.5px solid ${cat.color}` }}
                      >
                        <div className="size-2.5 rounded-full" style={{ background: cat.color }} />
                      </div>
                      <button
                        onClick={() => { handleWaypointClick(wp); setActiveDrawer(null); }}
                        className="flex-1 text-left"
                        data-testid={`waypoint-item-${wp.id}`}
                      >
                        <div className="text-sm font-medium">{wp.name}</div>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          {cat.label}
                          {wp.description && ` — ${wp.description.slice(0, 40)}`}
                        </div>
                      </button>
                      <button
                        onClick={() => handleDeleteWaypoint(wp.id)}
                        className="size-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                        data-testid={`delete-waypoint-${wp.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DrawerContent>
      </Drawer>

      {/* Download Area Drawer */}
      <Drawer open={activeDrawer === 'download'} onOpenChange={(o) => !o && setActiveDrawer(null)}>
        <DrawerContent className="glass-panel border-white/10" data-testid="download-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <Download size={18} /> オフライン用ダウンロード
            </DrawerTitle>
            <DrawerDescription>
              表示中のエリアのマップタイルを保存します
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 space-y-4">
            {mapBounds && (
              <div className="space-y-3">
                <div className="p-3 rounded-xl bg-white/5 space-y-2">
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>現在のズーム</span>
                    <span className="text-white font-mono">{mapBounds.zoom}</span>
                  </div>
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>ダウンロード範囲</span>
                    <span className="text-white font-mono">Z{mapBounds.zoom}-{MAX_ZOOM_PER_LAYER[tileLayer] || 18}</span>
                  </div>
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>推定タイル数</span>
                    <span className="text-white font-mono">
                      {(() => {
                        const maxZoom = MAX_ZOOM_PER_LAYER[tileLayer] || 18;
                        const zoomLevels = [];
                        for (let z = mapBounds.zoom; z <= maxZoom; z++) zoomLevels.push(z);
                        return calculateTileCount(mapBounds, zoomLevels).toLocaleString();
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>タイルソース</span>
                    <span className="text-white">{TILE_OPTIONS.find(t => t.key === tileLayer)?.label}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DrawerFooter>
            <Button
              onClick={handleDownloadArea}
              disabled={isDownloading || !mapBounds}
              className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold gap-2"
              data-testid="start-download-btn"
            >
              {isDownloading ? (
                <><Loader2 size={18} className="animate-spin" /> ダウンロード中...</>
              ) : (
                <><Download size={18} /> ダウンロード開始</>
              )}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Settings Drawer */}
      <Drawer open={activeDrawer === 'settings'} onOpenChange={(o) => { if (!o) setActiveDrawer(null); else refreshCacheStats(); }}>
        <DrawerContent className="glass-panel border-white/10" data-testid="settings-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">
              設定
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-4">
            {user ? (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-white/5">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{user.email}</div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => { logout(); setActiveDrawer(null); setWaypoints([]); setTracks([]); }}
                  className="w-full justify-start gap-2 text-zinc-400 hover:text-red-400"
                  data-testid="logout-btn"
                >
                  <LogOut size={16} /> ログアウト
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => { setActiveDrawer(null); navigate('/auth'); }}
                className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold gap-2"
                data-testid="goto-login-btn"
              >
                <LogIn size={18} /> ログイン / 登録
              </Button>
            )}

            {/* Cache Management */}
            <div className="pt-2 border-t border-white/5">
              <div className="text-xs tracking-widest uppercase text-muted-foreground mb-3 flex items-center gap-1.5">
                <HardDrive size={12} /> マップキャッシュ
              </div>
              <div className="space-y-2">
                <div className="p-3 rounded-xl bg-white/5 space-y-2">
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>キャッシュ済みタイル</span>
                    <span className="text-white font-mono" data-testid="cache-tile-count">{cacheStats.tileCount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>使用ストレージ</span>
                    <span className="text-white font-mono" data-testid="cache-size">{formatCacheSize(cacheStats.estimatedSizeKB)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>接続状態</span>
                    <span className={`font-medium ${isOnline ? 'text-lime-400' : 'text-yellow-400'}`} data-testid="connection-status">
                      {isOnline ? 'オンライン' : 'オフライン'}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleClearCache}
                  className="w-full justify-start gap-2 text-zinc-400 hover:text-red-400 text-sm"
                  data-testid="clear-cache-btn"
                >
                  <Trash2 size={14} /> キャッシュをクリア
                </Button>
              </div>
            </div>

            <div className="pt-2 border-t border-white/5">
              <div className="text-xs tracking-widest uppercase text-muted-foreground mb-3">情報</div>
              <div className="space-y-2 text-xs text-zinc-400">
                <div className="flex justify-between">
                  <span>ウェイポイント数</span>
                  <span className="text-white font-medium">{waypoints.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>トラック数</span>
                  <span className="text-white font-medium">{tracks.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>マップタイル</span>
                  <span className="text-white font-medium">{TILE_OPTIONS.find(t => t.key === tileLayer)?.label}</span>
                </div>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
